import type { ProethicaModuleSummary } from "@constants/demo/session-collection/proethica";
import type { ItemState, ItemStates } from "@features/session";
import type { MixedItemTag } from "@memorang/types";
import type {
	BaseItemContent,
	BaseItemSchema,
	Choice,
	FlashcardEntry,
	SituationCard,
} from "@memorang/types/src/session/new-session";
import { type ElementCompact, js2xml } from "xml-js";

type ModuleLanding = NonNullable<BaseItemContent["moduleLanding"]>;

export type ModuleItemStats = {
	score: number;
	timeTaken?: number | null;
};

export type ModulePerformanceContext = {
	statsById?: Map<number, ModuleItemStats>;
	itemStates?: ItemStates;
};

type UserResult = "correct" | "partially_correct" | "incorrect" | "skipped";

type UserResultAttributes = {
	user_result: UserResult;
	score?: string;
	time_taken_seconds?: string;
};

export const buildReportStatsById = (
	tagDetails?: MixedItemTag[],
): Map<number, ModuleItemStats> => {
	const statsById = new Map<number, ModuleItemStats>();
	for (const tag of tagDetails ?? []) {
		for (const item of tag.items ?? []) {
			if (item.stats) {
				statsById.set(item.id, {
					score: item.stats.score,
					timeTaken: item.stats.timeTaken,
				});
			}
		}
	}
	return statsById;
};

// js2xml escapes text nodes but leaves bare "&" and "<" in attribute values
// (invalid XML). Quotes are already "&quot;" by the time this hook runs, so
// only entity-free ampersands may be escaped.
const escapeAttributeValue = (value: string) =>
	String(value)
		.replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[\da-fA-F]+);)/g, "&amp;")
		.replace(/</g, "&lt;");

const serializeList = (
	entries?: { title: string; description: string }[],
): string | undefined =>
	entries?.length
		? entries.map((e) => `- **${e.title}**: ${e.description}`).join("\n")
		: undefined;

const serializeCards = (cards?: FlashcardEntry[]): string | undefined =>
	cards?.length
		? cards.map((c) => `- **${c.front.text}**: ${c.back}`).join("\n")
		: undefined;

const serializeSituations = (
	situations?: SituationCard[],
): string | undefined => {
	if (!situations?.length) {
		return undefined;
	}
	return situations
		.map((s) => {
			const outcomes = s.back.outcomes
				.map((o) => {
					const front = o.front?.trim();
					return front
						? `    - ${o.label}: ${front}\n      Outcome: ${o.content}`
						: `    - ${o.label}: ${o.content}`;
				})
				.join("\n");
			return `- **${s.front.title}**: ${s.front.text}\n${outcomes}`;
		})
		.join("\n");
};

const serializeSimulation = (
	simulation?: BaseItemContent["simulation"],
): string | undefined => {
	if (!simulation) {
		return undefined;
	}
	const { screenTitle, scenarioText, objectives } = simulation;
	const objectiveLine = objectives?.length
		? `\nObjectives: ${objectives.join(", ")}`
		: "";
	return `Simulation: ${screenTitle}\n${scenarioText}${objectiveLine}`;
};

const buildLearningObjectives = (
	objectives?: ModuleLanding["learningObjectives"],
): ElementCompact | undefined => {
	if (!objectives?.items?.length) {
		return undefined;
	}
	return {
		...(objectives.intro ? { intro: { _text: objectives.intro } } : {}),
		objective: objectives.items.map((o) => ({ _text: o })),
	};
};

const buildOutline = (
	outline?: ModuleLanding["outline"],
): ElementCompact | undefined => {
	if (!outline?.sections?.length) {
		return undefined;
	}
	return {
		section: outline.sections.map((section) => ({
			_attributes: { title: section.title },
			item: section.items.map((item) => ({
				_text: typeof item === "string" ? item : item.title,
			})),
		})),
	};
};

const itemState = (
	item: BaseItemSchema,
	performance?: ModulePerformanceContext,
): ItemState | undefined =>
	item.id ? performance?.itemStates?.get(item.id) : undefined;

const resolveUserResult = (
	stats?: ModuleItemStats,
	state?: ItemState,
): UserResult | undefined => {
	if (state && !state.selectedChoiceIds?.length) {
		return "skipped";
	}
	if (!stats) {
		return undefined;
	}
	if (stats.score >= 100) {
		return "correct";
	}
	return stats.score > 0 ? "partially_correct" : "incorrect";
};

const userResultAttributes = (
	item: BaseItemSchema,
	performance?: ModulePerformanceContext,
): UserResultAttributes | undefined => {
	if (!item.id) {
		return undefined;
	}
	const stats = performance?.statsById?.get(item.id);
	const userResult = resolveUserResult(stats, itemState(item, performance));
	if (!userResult) {
		return undefined;
	}
	return {
		user_result: userResult,
		...(userResult === "partially_correct" && stats
			? { score: String(stats.score) }
			: {}),
		...(typeof stats?.timeTaken === "number"
			? { time_taken_seconds: String(stats.timeTaken) }
			: {}),
	};
};

const buildChoices = (
	choices: Choice[],
	selectedChoiceIds?: string[],
): ElementCompact => ({
	choice: choices.map((choice) => {
		const attributes: Record<string, string> = {};
		if (choice.isCorrect) {
			attributes.correct = "true";
		}
		if (selectedChoiceIds?.includes(choice.id)) {
			attributes.user_selected = "true";
		}
		return {
			...(Object.keys(attributes).length ? { _attributes: attributes } : {}),
			_text: choiceText(choice),
		};
	}),
});

const activityTitle = (item: BaseItemSchema): string =>
	item.metadata?.title ??
	item.metadata?.taskType ??
	item.metadata?.type ??
	"Activity";

const choiceText = (choice: Choice): string => choice.text ?? choice.stem ?? "";

// correct_choice only when missed - user_selected_choice already answers it
// when correct.
const buildQuestionRecap = (
	item: BaseItemSchema,
	performance?: ModulePerformanceContext,
): ElementCompact | undefined => {
	const resultAttributes = userResultAttributes(item, performance);
	if (!resultAttributes) {
		return undefined;
	}
	const choices = item.content?.choices ?? [];
	const recap: ElementCompact = {
		_attributes: {
			...(item.id ? { id: String(item.id) } : {}),
			title: activityTitle(item),
			...resultAttributes,
		},
	};
	const selectedChoiceIds = itemState(item, performance)?.selectedChoiceIds;
	const selectedTexts = selectedChoiceIds?.length
		? choices
				.filter((choice) => selectedChoiceIds.includes(choice.id))
				.map(choiceText)
		: [];
	if (selectedTexts.length) {
		recap.user_selected_choice = selectedTexts.map((text) => ({
			_text: text,
		}));
	}
	if (resultAttributes.user_result !== "correct") {
		const correctTexts = choices
			.filter((choice) => choice.isCorrect)
			.map(choiceText);
		if (correctTexts.length) {
			recap.correct_choice = correctTexts.map((text) => ({ _text: text }));
		}
	}
	return recap;
};

const buildQuestion = (
	item: BaseItemSchema,
	content: BaseItemContent,
	performance?: ModulePerformanceContext,
): ElementCompact => {
	const selectedChoiceIds = itemState(item, performance)?.selectedChoiceIds;
	const question: ElementCompact = {};
	const resultAttributes = userResultAttributes(item, performance);
	if (resultAttributes) {
		question._attributes = resultAttributes;
	}
	if (content.questionStem) {
		question.stem = { _text: content.questionStem };
	}
	if (content.choices?.length) {
		question.choices = buildChoices(content.choices, selectedChoiceIds);
	}
	if (content.feedback) {
		question.feedback = { _text: content.feedback };
	}
	return question;
};

const buildActivity = (
	item: BaseItemSchema,
	performance?: ModulePerformanceContext,
): ElementCompact | undefined => {
	const content = item.content;
	if (!content) {
		return undefined;
	}
	const title = activityTitle(item);
	const contentMarkdown = [
		content.stem,
		content.lessonText,
		content.scenarioPractice?.scenario,
		serializeList(content.principles),
		serializeList(content.steps),
		serializeCards(content.cards),
		serializeSituations(content.situations),
		serializeSimulation(content.simulation),
	]
		.filter(Boolean)
		.join("\n");
	const hasQuestion = Boolean(content.questionStem || content.choices?.length);
	if (!(contentMarkdown || hasQuestion)) {
		return undefined;
	}

	const activity: ElementCompact = {
		_attributes: {
			...(item.id ? { id: String(item.id) } : {}),
			title,
		},
	};
	if (contentMarkdown) {
		activity.content = { _text: contentMarkdown };
	}
	if (hasQuestion) {
		activity.question = buildQuestion(item, content, performance);
	}
	return activity;
};

const buildModuleLandingParts = (landing: ModuleLanding): ElementCompact => {
	const parts: ElementCompact = {};
	const moduleInfo = landing.module;
	const attributes = {
		...(moduleInfo?.title ? { title: moduleInfo.title } : {}),
		...(moduleInfo?.label ? { label: moduleInfo.label } : {}),
	};
	if (Object.keys(attributes).length) {
		parts._attributes = attributes;
	}
	if (landing.lesson?.text) {
		parts.overview = { _text: landing.lesson.text };
	}
	const learningObjectives = buildLearningObjectives(
		landing.learningObjectives,
	);
	if (learningObjectives) {
		parts.learning_objectives = learningObjectives;
	}
	const outline = buildOutline(landing.outline);
	if (outline) {
		parts.outline = outline;
	}
	return parts;
};

// The analysis agent's only session context for praxis (backend skips the
// session_data message when additionalContext is present). Structure is XML,
// lesson bodies stay markdown; a <user_results> recap closes the document
// since LLMs attend best to recent content. Selections come from in-memory
// itemStates and are absent on cold load - builders degrade to content +
// report stats. The <module_context> root lives here (backend appends
// additionalContext verbatim).
export const buildProethicaModuleContext = (
	sessionItems: BaseItemSchema[],
	moduleSummary?: ProethicaModuleSummary,
	performance?: ModulePerformanceContext,
): string => {
	if (!sessionItems?.length) {
		return "";
	}

	const moduleElement: ElementCompact = {};

	const landing = sessionItems.find((item) => item.content?.moduleLanding)
		?.content?.moduleLanding;
	if (landing) {
		Object.assign(moduleElement, buildModuleLandingParts(landing));
	}

	const activities = sessionItems
		.filter((item) => !item.content?.moduleLanding)
		.map((item) => buildActivity(item, performance))
		.filter((activity): activity is ElementCompact => Boolean(activity));
	if (activities.length) {
		moduleElement.activities = { activity: activities };
	}

	if (moduleSummary?.text) {
		moduleElement.module_summary = {
			...(moduleSummary.title
				? { _attributes: { title: moduleSummary.title } }
				: {}),
			_text: moduleSummary.text,
		};
	}

	if (!Object.keys(moduleElement).length) {
		return "";
	}

	const scoredQuestions = sessionItems
		.filter((item) => !item.content?.moduleLanding)
		.map((item) => buildQuestionRecap(item, performance))
		.filter((recap): recap is ElementCompact => Boolean(recap));

	return js2xml(
		{
			module_context: {
				module: moduleElement,
				...(scoredQuestions.length
					? { user_results: { question: scoredQuestions } }
					: {}),
			},
		},
		{ compact: true, spaces: 2, attributeValueFn: escapeAttributeValue },
	);
};
